/** 회사정보(institution) 관리 API — /api/admin/institution (BFF → chat-server SSOT proxy). */
import { apiClient } from './client';

export interface Institution {
  id: string;
  code: string;
  name: string;
  domain: string | null;
  isActive: boolean;
  createdAt: string;
  // 회사정보 프로필 (install-bootstrap Part A) — 사업자등록번호는 표시용 ###-##-##### 포맷.
  businessRegNo: string | null;
  representativeName: string | null;
  businessType: string | null;
  businessItem: string | null;
  postalCode: string | null;
  address: string | null;
  addressDetail: string | null;
  phone: string | null;
  fax: string | null;
  companyEmail: string | null;
  /** 회사 로고 (2026-08-12) — `wclogo:{ts}` | http(s) URL | null(미등록). 사용자 사이트 브랜드에 반영. */
  logoUrl: string | null;
}

/** 현재 등록된 로고 미리보기 — base64(chat-server MinIO 객체) 또는 외부 URL. null = 미등록. */
export type InstitutionLogo =
  | { contentType: string; data: string; url?: undefined }
  | { url: string; contentType?: undefined; data?: undefined };

/** PATCH 가능 필드 — 전부 optional. name 외 null = 값 비움. */
export type InstitutionPatch = Partial<{
  name: string;
  domain: string | null;
  businessRegNo: string | null;
  representativeName: string | null;
  businessType: string | null;
  businessItem: string | null;
  postalCode: string | null;
  address: string | null;
  addressDetail: string | null;
  phone: string | null;
  fax: string | null;
  companyEmail: string | null;
}>;

export async function getInstitution(): Promise<Institution> {
  const { data } = await apiClient.get<{ institution: Institution }>('/admin/institution');
  return data.institution;
}

export async function updateInstitution(patch: InstitutionPatch): Promise<Institution> {
  const { data } = await apiClient.patch<{ institution: Institution }>('/admin/institution', patch);
  return data.institution;
}

// ── 회사 로고 (2026-08-12) ────────────────────────────────────────────────────
// 업로드는 base64(JSON) — BFF proxy 가 JSON 전용이라 바이너리 경로가 없다(admin-backend
// routes/institution.ts 주석 참조). 서버 상한 1MB 는 업로드 전 리사이즈로 맞춘다.

/** 현재 로고 조회(미리보기). 미등록이면 null. */
export async function getInstitutionLogo(): Promise<InstitutionLogo | null> {
  const { data } = await apiClient.get<{ logo: InstitutionLogo | null }>('/admin/institution/logo');
  return data.logo ?? null;
}

/** 로고 등록/교체 — 갱신된 institution 반환(logoUrl 포함). */
export async function uploadInstitutionLogo(contentType: string, base64: string): Promise<Institution> {
  const { data } = await apiClient.put<{ institution: Institution }>(
    '/admin/institution/logo',
    { contentType, data: base64 },
  );
  return data.institution;
}

/** 로고 삭제 — 사용자 화면은 기본 심볼로 복귀. */
export async function deleteInstitutionLogo(): Promise<Institution> {
  const { data } = await apiClient.delete<{ institution: Institution }>('/admin/institution/logo');
  return data.institution;
}
